import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, BookOpen, Target,
  Settings, LogOut, Menu, X, Bell, ChevronDown,
  TrendingUp, User, PiggyBank, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import '../../styles/layout.css';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
  {
    icon: PiggyBank, label: 'Budget Tracker',
    children: [
      { to: '/dashboard/budget/groceries', icon: ShoppingCart, label: 'Groceries' },
    ]
  },
  { to: '/dashboard/learn', icon: BookOpen, label: 'Financial Education' },
  { to: '/dashboard/goals', icon: Target, label: 'Savings Goals' },
  { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
];

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(true);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const displayName =
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="layout-root">
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <svg width="24" height="21" viewBox="0 0 32 28" fill="none">
              <rect x="0" y="8" width="6" height="20" rx="1" fill="#E8471E"/>
              <rect x="9" y="4" width="6" height="24" rx="1" fill="#E8471E"/>
              <rect x="18" y="0" width="6" height="28" rx="1" fill="#E8471E"/>
              <rect x="26" y="6" width="6" height="22" rx="1" fill="#C13010"/>
            </svg>
            <span className="sidebar-logo-text">SYNFIN</span>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            // Item with children (expandable section)
            if (item.children) {
              return (
                <div key={item.label}>
                  <button
                    className={`nav-item nav-item-parent${budgetOpen ? ' nav-item-parent-open' : ''}`}
                    onClick={() => setBudgetOpen(v => !v)}>
                    <item.icon size={18} />
                    <span>{item.label}</span>
                    <ChevronRight size={14}
                      className={`nav-chevron${budgetOpen ? ' nav-chevron-open' : ''}`} />
                  </button>
                  {budgetOpen && (
                    <div className="nav-children">
                      {item.children.map(child => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={({ isActive }) =>
                            `nav-item nav-item-child${isActive ? ' nav-item-active' : ''}`
                          }
                          onClick={() => setSidebarOpen(false)}>
                          <child.icon size={16} />
                          <span>{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Regular nav item
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `nav-item${isActive ? ' nav-item-active' : ''}`
                }
                onClick={() => setSidebarOpen(false)}>
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-version">Alpha v1.1.0</div>
        </div>
      </aside>

      {/* Main content */}
      <div className="layout-main">
        {/* Topbar */}
        <header className="topbar">
          <button className="topbar-menu-btn"
            onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>

          <div className="topbar-brand">
            <TrendingUp size={18} color="#E8471E" />
            <span>Personal Finance</span>
          </div>

          <div className="topbar-right">
            <button className="topbar-icon-btn" aria-label="Notifications">
              <Bell size={18} />
              <span className="notification-dot" />
            </button>

            <div className="user-menu-wrapper">
              <button className="user-menu-trigger"
                onClick={() => setUserMenuOpen(v => !v)}>
                <div className="user-avatar">{initials}</div>
                <span className="user-name">{displayName}</span>
                <ChevronDown size={16}
                  className={userMenuOpen ? 'chevron-open' : ''} />
              </button>

              {userMenuOpen && (
                <div className="user-menu-dropdown">
                  <div className="user-menu-info">
                    <div className="user-avatar user-avatar-lg">{initials}</div>
                    <div>
                      <p className="user-menu-name">{displayName}</p>
                      <p className="user-menu-email">{user?.email}</p>
                    </div>
                  </div>
                  <div className="user-menu-divider" />
                  <button className="user-menu-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/dashboard/settings');
                    }}>
                    <User size={15} /> Profile & Settings
                  </button>
                  <button
                    className="user-menu-item user-menu-item-danger"
                    onClick={handleSignOut}>
                    <LogOut size={15} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="layout-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
